import { pdfFooterHeightPx } from '../utils/pdfLetterhead';
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Checkbox, CircularProgress, DialogActions, DialogContent, DialogTitle, Modal, ModalDialog, Stack, Typography } from '@mui/joy';
import SupplierSelector from '../../../components/SupplierSelector';
import { directSupabase } from '../../../lib/supabaseClient';
import { buildTransientOrder } from '../../../hooks/useOrder';
import {
  getGraphClient,
  getWindowOrderEmailSubject,
  isSignedIn,
  sendOrderEmail,
  signIn,
} from '../../../utils/graphService';
import { convertWindowItemsForOrder } from '../../../types/windowOrder';
import { validateWindowImages } from '../../../utils/windowHelpers';
import { generateUuid } from '../../../utils/uuid-helper';
import { pdfExportConfigService } from '../services/pdfExportConfig.service';
import type { WindowBasketItem } from '../../../hooks/useWindowBasket';
import type { Database } from '../../../types/supabase';
import type { DocumentKind } from '../types/documents.types';
import { buildSupplierPdfDraftContent, buildSupplierPdfFileName } from '../config/supplierPdfDraftContent';
import { computeBayGeometry, createDefaultBayConfig, normalizeBayConfig } from '../../../utils/bayGeometry';
import {
  buildLatestWindowGeneratorPayloadFromMeta,
  renderWindowGeneratorImageBlobs,
} from '../services/visualDesignerRenderAssets.service';
import { MULTI_LEAF_DOOR_TYPE_IDS } from '../utils/doorTypes';
import { getDesignerSummaryKeyedRows, type DesignerSummaryRow, type DesignerSummaryRowKey } from '../utils/visualDesignerSummary';
import { emailActivity } from '../../../lib/emailActivityService';
import { buildSupplierTransmissionMetadata } from '../utils/supplierTransmission';
import { visualDesignerSurveysService } from '../services/visualDesignerSurveys.service';
import { isVisualDesignerLine } from '../utils/visualDesignerSurveyClone';
import { emitActivityEventBackground } from '../../../services/activityEvents.service';

type SupplierRow = Database['public']['Tables']['suppliers']['Row'];
const WINDOW_GENERATOR_PX2MM = 2.5;

type Props = {
  open: boolean;
  onClose: () => void;
  documentId?: string;
  activeVersionId?: string | null;
  customerReference: string;
  customerName: string;
  organisationName?: string | null;
  organisationId?: string | null;
  documentKind?: DocumentKind;
  projectId?: string | null;
  surveyId?: string | null;
  surveyLabel?: string | null;
  documentVersionId?: string | null;
  documentVersionNumber?: number | null;
  sourceLines?: Array<{
    id?: string;
    name?: string;
    qty?: number;
    materialId?: string | null;
    metaJson?: any;
  }> | null;
  preferredSupplierId?: string | null;
  onTransmitted?: () => void;
};

function toPublicImageUrl(path?: string): string {
  if (!path) return '';
  const p = String(path);
  if (p.startsWith('http://') || p.startsWith('https://')) return p;
  if (p.startsWith('/')) return p;
  return `/${p}`;
}

function toAbsoluteAssetUrl(path?: string): string {
  if (!path) return '';
  const raw = String(path).trim();
  if (!raw) return '';
  if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
  const normalized = raw.startsWith('/') ? raw : `/${raw}`;
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}${normalized}`;
  }
  return normalized;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
}

const PROFILE_LABEL_BY_CODE: Record<string, string> = {
  chamfered: 'Chamfered Profile',
  sculptured: 'Sculptured Profile',
  'flush-sash': 'Flush Sash',
  'aluminium-contemporary': 'Contemporary',
  'aluminium-flush': 'Flush',
  'aluminium-flush-contemporary': 'Flush Contemporary',
  'aluminium-stepped-crittal': 'Stepped (Crittal)',
  'aluminium-flush-stepped': 'Flush Stepped',
  'timber-flush': 'Flush',
  'timber-timberweld-tm': 'Timberweld (TM)',
  'timber-casement': 'Casement',
  'aluminium-add': 'Add',
};

const BEADING_LABEL_BY_CODE: Record<string, string> = {
  chamfered: 'Chamfered (Bevelled)',
  sculptured: 'Sculptured (Ovolo)',
  'flush-sash': 'Flush Sash',
};

function deriveVariant(meta: any): { profileType?: string; beadingType?: string } {
  const code = String(meta?.specs?.variant_code || '').trim().toLowerCase();
  const profileFromCode = PROFILE_LABEL_BY_CODE[code];
  if (profileFromCode) {
    return {
      profileType: profileFromCode,
      beadingType: BEADING_LABEL_BY_CODE[code] || '',
    };
  }

  const labelRaw = String(meta?.specs?.variant || '').trim();
  const label = labelRaw.toLowerCase();
  if (labelRaw) {
    return {
      profileType: labelRaw,
      beadingType:
        (label.includes('ovolo') || label.includes('sculpt')) ? 'Sculptured (Ovolo)'
          : (label.includes('chamf') || label.includes('bevel')) ? 'Chamfered (Bevelled)'
            : '',
    };
  }
  return {};
}

const MATERIAL_LABEL_BY_ID: Record<string, string> = {
  upvc: 'uPVC',
  aluminium: 'Aluminium',
  timber: 'Timber',
  composite: 'Composite',
};

function resolveSingleMaterialName(meta: any, lineMaterialId?: string | null): string {
  const fromLine = MATERIAL_LABEL_BY_ID[String(lineMaterialId || '').trim().toLowerCase()];
  if (fromLine) return fromLine;

  const explicitName = String(meta?.window_material?.name || '').trim();
  if (explicitName) {
    const canonicalExplicit = MATERIAL_LABEL_BY_ID[explicitName.toLowerCase()];
    if (canonicalExplicit) return canonicalExplicit;
    return explicitName;
  }

  const rawCandidates: string[] = [];
  const storedMaterialId = String(meta?.window_material?.id || '').trim();
  if (storedMaterialId) rawCandidates.push(storedMaterialId);
  const normalizedLineMaterialId = String(lineMaterialId || '').trim().toLowerCase();
  if (normalizedLineMaterialId) rawCandidates.push(normalizedLineMaterialId);

  const legacyMaterialIds = Array.isArray(meta?.window_material_ids)
    ? meta.window_material_ids
    : (Array.isArray(meta?.windowMaterialIds) ? meta.windowMaterialIds : []);
  // Legacy material arrays can contain "all allowed materials" for a style (not selected value),
  // so only use this source when it is unambiguous.
  if (legacyMaterialIds.length === 1) {
    const next = String(legacyMaterialIds[0] || '').trim();
    if (next) rawCandidates.push(next);
  }

  // Back-compat: very old records stored material on window_type.id.
  const legacyTypeId = String(meta?.window_type?.id || '').trim().toLowerCase();
  if (legacyTypeId === 'upvc' || legacyTypeId === 'aluminium' || legacyTypeId === 'timber' || legacyTypeId === 'composite') {
    rawCandidates.push(legacyTypeId);
  }

  for (const raw of rawCandidates) {
    const tokens = String(raw)
      .toLowerCase()
      .split(/[,|;/]+/)
      .map((t) => t.trim())
      .filter(Boolean);
    for (const token of tokens) {
      const label = MATERIAL_LABEL_BY_ID[token];
      if (label) return label;
    }
  }

  return '';
}

function getWindowGeneratorPayloadFromMeta(meta: any): any {
  return buildLatestWindowGeneratorPayloadFromMeta(meta);
}

function isBayLikeMeta(meta: any): boolean {
  const payload = getWindowGeneratorPayloadFromMeta(meta);
  if (!payload || typeof payload !== 'object') return false;
  const rootPreset = String(payload?.root?.preset || '').trim().toLowerCase();
  return rootPreset === 'bay';
}

function isBayLikePayload(payload: any): boolean {
  if (!payload || typeof payload !== 'object') return false;
  const rootPreset = String(payload?.root?.preset || '').trim().toLowerCase();
  return rootPreset === 'bay';
}

function deriveBayMetricsFromPayload(payload: any): {
  tangent: number;
  isCurvedBow: boolean;
  projectionDepth: number;
  facetWidths: number[];
  jointAngles: number[];
} | null {
  if (!isBayLikePayload(payload)) return null;
  try {
    const fallback = createDefaultBayConfig({
      tangentWidthMm: Math.max(200, Math.round(Number(payload?.w || 400) * WINDOW_GENERATOR_PX2MM)),
      heightMm: Math.max(200, Math.round(Number(payload?.h || 300) * WINDOW_GENERATOR_PX2MM)),
    });
    const cfg = normalizeBayConfig(payload?.presentation?.bayConfig || fallback, {
      tangentWidthMm: Math.max(200, Math.round(Number(payload?.w || 400) * WINDOW_GENERATOR_PX2MM)),
      heightMm: Math.max(200, Math.round(Number(payload?.h || 300) * WINDOW_GENERATOR_PX2MM)),
    });
    const geo = computeBayGeometry(cfg);
    const isCurvedBow = String((cfg as any)?.bayType || '').trim().toUpperCase() === 'CURVED_BOW' || geo?.derived?.hasCurvedFace === true;
    const tangent = Math.round(Number(geo?.derived?.tangentWidth || (cfg as any)?.tangentWidth || 0)) || 0;
    const projectionDepth = isCurvedBow ? (Math.round(Number(geo?.derived?.projection || (cfg as any)?.projectionDepth || 0)) || 0) : 0;
    const facetWidths = Array.isArray((cfg as any)?.facets)
      ? (cfg as any).facets
          .map((f: any) => Math.round(Number(f?.width || 0)))
          .filter((n: number) => Number.isFinite(n) && n > 0)
      : [];
    const jointAngles = Array.isArray(geo?.joints)
      ? geo.joints
          .map((j: any) => Number(j?.manualInternalAngle ?? j?.derivedInternalAngle))
          .filter((n: number) => Number.isFinite(n))
          .map((n: number) => Math.round(n))
      : [];
    return { tangent, isCurvedBow, projectionDepth, facetWidths, jointAngles };
  } catch {
    return null;
  }
}

function deriveOpensSummaryFromPayload(payload: any, fallbackWindowType?: string): string {
  if (!payload || typeof payload !== 'object') {
    return String(fallbackWindowType || '').trim().toLowerCase() === 'door' ? 'Door' : '';
  }
  const paneKinds = payload?.presentation?.paneHandleKinds;
  if (paneKinds && typeof paneKinds === 'object') {
    const kinds = Object.values(paneKinds)
      .map((v: any) => String(v || '').trim().toLowerCase())
      .filter(Boolean);
    const hasDoor = kinds.includes('door');
    const hasWindow = kinds.includes('window');
    if (hasDoor && hasWindow) return 'Door + Window';
    if (hasDoor) return 'Door';
    if (hasWindow) return 'Window';
  }
  const wt = String(fallbackWindowType || '').trim().toLowerCase();
  return wt === 'door' || wt === 'upvc-door' || (MULTI_LEAF_DOOR_TYPE_IDS as readonly string[]).includes(wt) ? 'Door' : '';
}

function deriveCillPlacementLabelFromPayload(payload: any, optionData?: any): string {
  const root = payload?.root;
  const isUnionShapeFromPayload = !!(root && root.type === 'rectUnion' && Array.isArray(root.regions) && root.regions.length > 1);
  if (!isUnionShapeFromPayload) return '';
  const explicitLabel = String(optionData?.cill_union_mode_label || '').trim();
  if (explicitLabel) return explicitLabel;
  const pres = payload?.presentation || {};
  const cillOpt = String(optionData?.cill_option || '').trim().toLowerCase();
  const cillActiveByOption = cillOpt === '150' || cillOpt === '180' || cillOpt === 'stub';
  const cillActiveByPayload = !!pres?.cillOn;
  if (!cillActiveByOption && !cillActiveByPayload) return '';
  const rawMode = String(optionData?.cill_union_mode ?? pres?.cillUnionMode ?? pres?.cill_union_mode ?? '').trim().toLowerCase();
  const includeUpper = (optionData?.cill_union_include_upper === true) || pres?.cillUnionIncludeUpper === true;
  const normalized =
    rawMode === 'door_only' || rawMode === 'door-only' || rawMode === 'door only' ? 'door_only'
    : rawMode === 'window_only' || rawMode === 'window-only' || rawMode === 'window only' ? 'window_only'
    : rawMode === 'both' || rawMode === 'door_window' || rawMode === 'door+window' ? 'both'
    : (includeUpper ? 'both' : 'door_only');
  if (normalized === 'window_only') return 'Window Only';
  if (normalized === 'both') return 'Door & Window';
  return 'Door Only';
}

async function blobToDataUrl(blob: Blob): Promise<string> {
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Failed to convert image blob to data URL'));
    reader.readAsDataURL(blob);
  });
}

async function blobToOptimizedJpegDataUrl(
  blob: Blob,
  opts?: { maxWidth?: number; quality?: number; background?: string }
): Promise<string> {
  const maxWidth = Math.max(320, Math.round(Number(opts?.maxWidth ?? 820)));
  const quality = Math.min(0.92, Math.max(0.55, Number(opts?.quality ?? 0.76)));
  const background = String(opts?.background || '#f5f7fb');

  const tempUrl = URL.createObjectURL(blob);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('Failed to load regenerated image blob'));
      el.src = tempUrl;
    });

    const srcW = Math.max(1, img.naturalWidth || img.width || maxWidth);
    const srcH = Math.max(1, img.naturalHeight || img.height || Math.round(maxWidth * 0.7));
    const scale = srcW > maxWidth ? (maxWidth / srcW) : 1;
    const outW = Math.max(1, Math.round(srcW * scale));
    const outH = Math.max(1, Math.round(srcH * scale));

    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2d context not available');

    // JPEG has no alpha channel, so paint a neutral paper-like background first.
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, outW, outH);
    ctx.drawImage(img, 0, 0, outW, outH);

    return canvas.toDataURL('image/jpeg', quality);
  } finally {
    URL.revokeObjectURL(tempUrl);
  }
}

export function visualDesignerLinesToWindowBasketItems(lines: any[]): WindowBasketItem[] {
  const now = Date.now();
  return (lines || []).map((line: any, idx: number) => {
    const meta = (line as any)?.metaJson || {};
    const style = meta?.window_style || {};
    const specs = meta?.specs || {};
    const options = specs?.options || {};

    // Back-compat: older schema may have stored material as "window_type"
    const storedTypeId = String(meta?.window_type?.id || '').trim();
    const windowType =
      storedTypeId === 'aluminium' || storedTypeId === 'timber'
        ? 'casement'
        : storedTypeId === 'upvc-door'
          ? 'door'
          : (storedTypeId || 'casement');

    const isUploadStyle = String(style?.source || '').trim().toLowerCase() === 'upload';
    const imagePath = toPublicImageUrl(style?.preview_path_external || style?.preview_path || style?.previewPath || '');
    const imagePathInternal = toPublicImageUrl(style?.preview_path_internal || '');
    const imagePresentationOutside = toPublicImageUrl(style?.preview_path_presentation_external || (isUploadStyle ? '' : imagePath));
    const imagePresentationInside = toPublicImageUrl(style?.preview_path_presentation_internal || (isUploadStyle ? '' : (imagePathInternal || imagePath)));
    const imageDesignOutside = toPublicImageUrl(style?.preview_path_design_external || (isUploadStyle ? '' : imagePath));
    const imageDesignInside = toPublicImageUrl(style?.preview_path_design_internal || (isUploadStyle ? '' : (imagePathInternal || imagePath)));
    const imageBaySchematic = isBayLikeMeta(meta)
      ? toPublicImageUrl(style?.preview_path_bay_schematic || style?.preview_path_plan_external || '')
      : '';

    const cillOpt = options?.cill_option;
    const cillIncluded = cillOpt === '150' || cillOpt === '180' || cillOpt === 'stub';
    const cillHeight = cillOpt === '150' ? 150 : cillOpt === '180' ? 180 : undefined;
    const cillType = cillOpt === 'stub' ? 'Stub' : (cillIncluded ? 'Regular' : '');

    const variant = deriveVariant({ specs });
    const beadingType = (() => {
      const bc = String((specs as any)?.beading_type_code || '').toLowerCase();
      if (BEADING_LABEL_BY_CODE[bc]) return BEADING_LABEL_BY_CODE[bc];
      const bl = String((specs as any)?.beading_type || '').toLowerCase();
      if (bl.includes('ovolo') || bl.includes('sculpt')) return 'Sculptured (Ovolo)';
      if (bl.includes('chamf') || bl.includes('bevel')) return 'Chamfered (Bevelled)';
      const vc = String((specs as any)?.variant_code || '').toLowerCase();
      if (BEADING_LABEL_BY_CODE[vc]) return BEADING_LABEL_BY_CODE[vc];
      return '';
    })();

    const materialName = resolveSingleMaterialName(meta, (line as any)?.materialId ?? null);
    const glassType =
      String((specs as any)?.glass_type || '').trim() ||
      (String((specs as any)?.glass_type_code || '').toLowerCase() === 'obscured' ? 'Obscured' : 'Clear');
    const baseNotes = String(specs?.notes || '').trim();
    const styleName = String(style?.name || '').trim();
    const handleColor = String(options?.handle_color || '').trim();
    const handleStyle = (() => {
      const label = String(options?.handle_style || '').trim();
      if (label) return label;
      const code = String((options as any)?.handle_style_code || '').trim().toLowerCase();
      if (!code) return '';
      if (code === 'monkey-tail') return 'Monkey Tail';
      if (code === 'pear-drop') return 'Pear Drop';
      if (code === 'bulb-end') return 'Bulb End';
      if (code === 'cockspur') return 'Cockspur';
      if (code === 'inline') return 'Inline';
      if (code === 'offset') return 'Offset';
      if (code === 'lever') return 'Lever';
      if (code === 'teardrop') return 'Teardrop';
      return '';
    })();
    const designerSummaryRows = getDesignerSummaryKeyedRows({ ...meta, specs });
    const summaryValue = (key: DesignerSummaryRowKey): string =>
      designerSummaryRows.find((r) => r.key === key)?.value || '';
    const windowHingeType = summaryValue('window_hinge_type');
    const astragalBarSide = (() => {
      const lbl = String((options as any)?.astragal_bar_side_label || '').trim();
      const code = String((options as any)?.astragal_bar_side || '').trim().toLowerCase();
      if (lbl) return lbl;
      if (code === 'none') return 'None';
      if (code === 'inside' || code === 'internal') return 'Inside';
      if (code === 'outside' || code === 'external') return 'Outside';
      if (code === 'both') return 'Both';
      return '';
    })();
    const hingePlacement = summaryValue('hinge_placement');
    const openDirection = summaryValue('open_direction');
    const trickleEnabled = options?.trickle_vents === true;
    const windowGeneratorPayload = getWindowGeneratorPayloadFromMeta(meta);
    const designDimsOn = (options as any)?.design_dims_on === true;
    const frameColorCode = String((specs as any)?.frame_color_code || '').trim() || undefined;
    const frameColorCodeExternal = String((specs as any)?.frame_color_code_external || '').trim() || undefined;
    const frameColorCodeInternal = String((specs as any)?.frame_color_code_internal || '').trim() || undefined;
    const frameColorExternalName = String((specs as any)?.frame_color_external || '').trim() || undefined;
    const frameColorInternalName = String((specs as any)?.frame_color_internal || '').trim() || undefined;
    const frameColorExternalHex = String((options as any)?.frame_color_hex_external || '').trim() || undefined;
    const frameColorInternalHex = String((options as any)?.frame_color_hex_internal || '').trim() || undefined;
    const isBay = isBayLikePayload(windowGeneratorPayload);
    const bayDerived = deriveBayMetricsFromPayload(windowGeneratorPayload);
    const opensSummary = deriveOpensSummaryFromPayload(windowGeneratorPayload, windowType);
    const cillPlacementLabel = deriveCillPlacementLabelFromPayload(windowGeneratorPayload, options);
    const isCurvedBay = bayDerived?.isCurvedBow === true;
    const bayTangentWidthMm = Number((options as any)?.bay_tangent_width_mm ?? bayDerived?.tangent ?? 0) || 0;
    const bayLegacyWidthMm = Number((options as any)?.bay_legacy_width_mm ?? 0) || 0;
    const bayProjectionDepthMm = isCurvedBay ? (Number((options as any)?.bay_projection_depth_mm ?? bayDerived?.projectionDepth ?? 0) || 0) : 0;
    const bayFacetWidthsMm = Array.isArray((options as any)?.bay_facet_widths_mm)
      ? ((options as any).bay_facet_widths_mm as any[])
          .map((n) => Number(n))
          .filter((n) => Number.isFinite(n))
          .map((n) => Math.round(n))
          .join(', ')
      : (bayDerived?.facetWidths?.length ? bayDerived.facetWidths.map((n) => Math.round(n)).join(', ') : '');
    const bayJointAnglesDeg = Array.isArray((options as any)?.bay_joint_angles_deg)
      ? ((options as any).bay_joint_angles_deg as any[])
          .map((n) => Number(n))
          .filter((n) => Number.isFinite(n))
          .map((n) => `${Math.round(n)}°`)
          .join(', ')
      : (bayDerived?.jointAngles?.length ? bayDerived.jointAngles.map((n) => `${Math.round(n)}°`).join(', ') : '');
    // Email {{notes}} should match the visual designer Notes field only — spec details use their own template vars.
    const notes = baseNotes || undefined;

    return {
      id: generateUuid(),
      windowType,
      windowStyle: Number(style?.id ?? 0) || Number(style?.styleId ?? 0) || 0,
      frameColor: String(specs?.frame_color || '').trim() || 'White',
      // Provide explicit style name for email templates (picked up via extras mapping)
      ...(styleName ? ({ styleName } as any) : {}),
      ...(materialName ? ({ windowMaterial: materialName } as any) : {}),
      ...(handleColor ? ({ handleColor } as any) : {}),
      ...(handleStyle ? ({ handleStyle } as any) : {}),
      ...(windowHingeType ? ({ hingeType: windowHingeType } as any) : {}),
      ...(astragalBarSide ? ({ astragalBarSide } as any) : {}),
      ...(hingePlacement ? ({ hingePlacement } as any) : {}),
      ...(openDirection ? ({ openDirection } as any) : {}),
      glassType,
      clearGlassType: 'float',
      quantity: Number(line?.qty ?? 1) || 1,
      price: 0,
      createdAt: now + idx,
      updatedAt: now + idx,
      imagePath,
      ...(imagePathInternal ? ({ imagePathInternal } as any) : {}),
      ...(imagePresentationOutside ? ({ imagePresentationOutside } as any) : {}),
      ...(imagePresentationInside ? ({ imagePresentationInside } as any) : {}),
      ...(imageDesignOutside ? ({ imageDesignOutside } as any) : {}),
      ...(imageDesignInside ? ({ imageDesignInside } as any) : {}),
      ...(isBay && imageBaySchematic ? ({ imageBaySchematic } as any) : {}),
      width: isBay
        ? (bayTangentWidthMm || (specs?.width_mm != null ? Number(specs.width_mm) : undefined))
        : (specs?.width_mm != null ? Number(specs.width_mm) : undefined),
      height: specs?.height_mm != null ? Number(specs.height_mm) : undefined,
      profileType: variant.profileType,
      beadingType: (beadingType as any) || variant.beadingType,
      cillRequired: false,
      cillIncluded,
      cillHeight,
      cillType: cillType as any,
      trickleVents: trickleEnabled,
      ...(options?.trickle_vents_position ? ({ trickleVentsPosition: options.trickle_vents_position } as any) : {}),
      ...(bayTangentWidthMm ? ({ bayTangentWidthMm } as any) : {}),
      ...(bayLegacyWidthMm ? ({ bayLegacyWidthMm } as any) : {}),
      ...(bayProjectionDepthMm ? ({ bayProjectionDepthMm } as any) : {}),
      ...(bayFacetWidthsMm ? ({ bayFacetWidthsMm } as any) : {}),
      ...(bayJointAnglesDeg ? ({ bayJointAnglesDeg } as any) : {}),
      ...(windowGeneratorPayload ? ({ windowGeneratorPayload } as any) : {}),
      ...(typeof (options as any)?.design_dims_on === 'boolean' ? ({ designDimsOn } as any) : {}),
      ...(frameColorCode ? ({ frameColorCode } as any) : {}),
      ...(frameColorCodeExternal ? ({ frameColorCodeExternal } as any) : {}),
      ...(frameColorCodeInternal ? ({ frameColorCodeInternal } as any) : {}),
      ...(frameColorExternalName ? ({ frameColorExternalName } as any) : {}),
      ...(frameColorInternalName ? ({ frameColorInternalName } as any) : {}),
      ...(frameColorExternalHex ? ({ frameColorExternalHex } as any) : {}),
      ...(frameColorInternalHex ? ({ frameColorInternalHex } as any) : {}),
      ...(opensSummary ? ({ opensSummary } as any) : {}),
      ...(cillPlacementLabel ? ({ cillPlacementLabel } as any) : {}),
      ...({ designerSummaryRows } as any),
      notes,
      reference: String(specs?.reference || '').trim() || `Window ${idx + 1}`,
    };
  });
}

export const SendVisualDesignerToSupplierModal: React.FC<Props> = ({
  open,
  onClose,
  documentId,
  activeVersionId,
  customerReference,
  customerName,
  organisationName,
  organisationId,
  documentKind,
  projectId,
  surveyId,
  surveyLabel,
  documentVersionId,
  documentVersionNumber,
  sourceLines,
  preferredSupplierId,
  onTransmitted,
}) => {
  const [savedVisualDesignerLines, setSavedVisualDesignerLines] = useState<any[]>([]);
  const [isLoadingLines, setIsLoadingLines] = useState(false);
  const [linesLoadError, setLinesLoadError] = useState<string | null>(null);

  const items = useMemo(() => visualDesignerLinesToWindowBasketItems(savedVisualDesignerLines), [savedVisualDesignerLines]);
  const summary = useMemo(() => {
    let totalQty = 0;
    let windowQty = 0;
    let doorQty = 0;
    for (const it of items) {
      const q = Math.max(1, Number((it as any)?.quantity ?? 1) || 1);
      totalQty += q;
      const wt = String((it as any)?.windowType || '').trim();
      if (wt === 'upvc-door' || wt === 'door' || (MULTI_LEAF_DOOR_TYPE_IDS as readonly string[]).includes(wt)) doorQty += q;
      else windowQty += q;
    }
    return { totalQty, windowQty, doorQty };
  }, [items]);

  const [supplierId, setSupplierId] = useState<string>('');
  const [isSending, setIsSending] = useState(false);
  const [isCreatingDraft, setIsCreatingDraft] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [status, setStatus] = useState<{ kind: 'idle' | 'success' | 'error'; message?: string }>({ kind: 'idle' });

  const [ignoreMissingImages, setIgnoreMissingImages] = useState(false);
  const [failedImages, setFailedImages] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    setStatus({ kind: 'idle' });
    setIgnoreMissingImages(false);
    setFailedImages([]);
    setLinesLoadError(null);
    if (preferredSupplierId) setSupplierId(preferredSupplierId);
  }, [open, preferredSupplierId]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const normalizeRows = (rows: any[]) => (rows || [])
      .filter((r: any) => String((r as any)?.metaJson?.feature || (r as any)?.meta_json?.feature || '') === 'visual_designer')
      .map((r: any, idx: number) => ({
        id: r.id || `vd-${idx}`,
        name: r.name,
        qty: Number(r.qty ?? 1) || 1,
        materialId: (r as any).materialId ?? (r as any).material_id ?? null,
        metaJson: r.metaJson || r.meta_json || null,
      }));

    (async () => {
      try {
        setIsLoadingLines(true);
        setLinesLoadError(null);

        // Prefer live survey items — same source of truth as Quotes / Estimates → Visual Designer.
        const liveSurveyId = String(surveyId || '').trim();
        if (liveSurveyId) {
          const items = await visualDesignerSurveysService.listItems(liveSurveyId);
          if (!cancelled) {
            setSavedVisualDesignerLines(
              items
                .filter(isVisualDesignerLine)
                .map((r: any, idx: number) => ({
                  id: r.id || `vd-${idx}`,
                  name: r.name,
                  qty: Number(r.qty ?? 1) || 1,
                  materialId: (r as any).materialId ?? (r as any).material_id ?? null,
                  metaJson: r.metaJson || r.meta_json || null,
                })),
            );
          }
          return;
        }

        if (Array.isArray(sourceLines)) {
          if (!cancelled) {
            setSavedVisualDesignerLines((sourceLines || []).map((r: any, idx: number) => ({
              id: r.id || `vd-${idx}`,
              name: r.name,
              qty: Number(r.qty ?? 1) || 1,
              materialId: (r as any).materialId ?? (r as any).material_id ?? null,
              metaJson: r.metaJson || r.meta_json || null,
            })));
          }
          return;
        }

        if (!documentId) {
          if (!cancelled) setSavedVisualDesignerLines([]);
          return;
        }

        const isInvoice = String(documentKind || '').toLowerCase() === 'invoice';
        let rows: any[] = [];

        if (isInvoice) {
          const { data, error } = await directSupabase
            .from('document_lines')
            .select('id, name, qty, sort_order, meta_json, material_id')
            .eq('document_id', documentId)
            .order('sort_order', { ascending: true });
          if (error) throw error;
          rows = Array.isArray(data) ? data : [];
        } else {
          let versionId = String(documentVersionId || activeVersionId || '').trim();
          if (!versionId) {
            const { data: docRow, error: docErr } = await directSupabase
              .from('documents')
              .select('active_version_id')
              .eq('id', documentId)
              .single();
            if (docErr) throw docErr;
            versionId = String((docRow as any)?.active_version_id || '').trim();
          }
          if (!versionId) {
            if (!cancelled) setSavedVisualDesignerLines([]);
            return;
          }
          const { data, error } = await directSupabase
            .from('document_version_lines')
            .select('id, name, qty, sort_order, meta_json, material_id')
            .eq('version_id', versionId)
            .order('sort_order', { ascending: true });
          if (error) throw error;
          rows = Array.isArray(data) ? data : [];
        }

        if (!cancelled) setSavedVisualDesignerLines(normalizeRows(rows));
      } catch (e: any) {
        console.error('Failed to load saved Visual Designer lines for supplier send', e);
        if (!cancelled) {
          setSavedVisualDesignerLines([]);
          setLinesLoadError(e?.message || 'Failed to load saved Visual Designer lines.');
        }
      } finally {
        if (!cancelled) setIsLoadingLines(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, documentId, activeVersionId, documentKind, documentVersionId, sourceLines, surveyId]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const failed = await validateWindowImages(items);
        setFailedImages(failed);
      } catch (e) {
        console.warn('Failed to validate images for supplier email', e);
        setFailedImages([]);
      }
    })();
  }, [open, items]);

  const shouldDisableDraft = () => {
    if (isSending || isCreatingDraft || isLoadingLines) return true;
    if (failedImages.length > 0 && !ignoreMissingImages) return true;
    if (!items.length) return true;
    return false;
  };

  const shouldDisableSend = () => {
    if (!supplierId) return true;
    if (isSending || isCreatingDraft || isLoadingLines) return true;
    if (failedImages.length > 0 && !ignoreMissingImages) return true;
    if (!items.length) return true;
    return false;
  };

  const ensureSupplierPdfImages = async (sourceItems: WindowBasketItem[]): Promise<WindowBasketItem[]> => {
    const out: WindowBasketItem[] = [];
    for (const it of sourceItems || []) {
      const payload = (it as any)?.windowGeneratorPayload;
      if (!payload) {
        out.push(it);
        continue;
      }
      let nextItem: WindowBasketItem = it;
      try {
        const payloadForRender: any = payload as any;
        // Regenerate all output visuals from one shared renderer path.
        const rendered = await renderWindowGeneratorImageBlobs(payloadForRender as any);

        const patch: any = {};
        if (rendered.designExternal) {
          patch.imageDesignOutside = await blobToOptimizedJpegDataUrl(rendered.designExternal, {
            maxWidth: 860,
            quality: 0.8,
          }).catch(async () => blobToDataUrl(rendered.designExternal));
        }
        if (rendered.presentationExternal) {
          patch.imagePresentationOutside = await blobToOptimizedJpegDataUrl(rendered.presentationExternal, {
            maxWidth: 700,
            quality: 0.74,
          }).catch(async () => blobToDataUrl(rendered.presentationExternal));
        }
        if (rendered.presentationInternal) {
          patch.imagePresentationInside = await blobToOptimizedJpegDataUrl(rendered.presentationInternal, {
            maxWidth: 700,
            quality: 0.74,
          }).catch(async () => blobToDataUrl(rendered.presentationInternal));
        }
        if (rendered.designInternal) {
          patch.imageDesignInside = await blobToOptimizedJpegDataUrl(rendered.designInternal, {
            maxWidth: 860,
            quality: 0.8,
          }).catch(async () => blobToDataUrl(rendered.designInternal));
        }
        if (rendered.baySchematic) {
          patch.imageBaySchematic = await blobToDataUrl(rendered.baySchematic);
        }
        if (Object.keys(patch).length > 0) {
          nextItem = { ...(nextItem as any), ...patch } as WindowBasketItem;
        }
      } catch {
        // Non-fatal: keep existing stored image.
      }
      const existingBay = String((nextItem as any)?.imageBaySchematic || '').trim();
      if (existingBay || !isBayLikePayload(payload)) {
        out.push(nextItem);
        continue;
      }
      out.push(nextItem);
    }
    return out;
  };

  const shouldDisablePdfTest = () => {
    if (isSending || isCreatingDraft || isExportingPdf || isLoadingLines) return true;
    if (failedImages.length > 0 && !ignoreMissingImages) return true;
    if (!items.length) return true;
    return false;
  };

  const escapeHtml = (value: unknown): string => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  const titleCase = (value: unknown): string => String(value ?? '')
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());

  const pdfImageSrc = (value: unknown): string => {
    const src = String(value ?? '').trim();
    if (!src) return '';
    if (src.startsWith('data:')) return src;
    return toAbsoluteAssetUrl(src);
  };

  const buildDedicatedSupplierPdfHtml = (orderForEmail: any, subject: string): string => {
    const itemsForPdf = Array.isArray(orderForEmail?.items) ? orderForEmail.items : [];
    const pick = (item: any, key: string): string => String((item as any)?.[key] ?? (item as any)?.extras?.[key] ?? '').trim();
    const qtyFor = (item: any): string => String((item as any)?.quantity ?? (item as any)?.qty ?? (item as any)?.extras?.quantity ?? 1);
    const boolFromUnknown = (value: unknown): boolean | null => {
      if (value === true || value === false) return value;
      const s = String(value ?? '').trim().toLowerCase();
      if (!s) return null;
      if (s === 'true' || s === 'yes' || s === '1' || s === 'on') return true;
      if (s === 'false' || s === 'no' || s === '0' || s === 'off') return false;
      return null;
    };
    const numFromUnknown = (value: unknown): number | null => {
      const n = Number(value);
      return Number.isFinite(n) ? n : null;
    };
    const mm = (value: unknown): string => {
      const n = Number(value);
      return Number.isFinite(n) && n > 0 ? `${Math.round(n)}mm` : '';
    };
    const summaryValueFor = (item: any) => {
      const rows: DesignerSummaryRow[] = Array.isArray((item as any)?.designerSummaryRows)
        ? (item as any).designerSummaryRows
        : Array.isArray((item as any)?.extras?.designerSummaryRows)
          ? (item as any).extras.designerSummaryRows
          : [];
      return (key: DesignerSummaryRowKey): string => rows.find((r) => r?.key === key)?.value || '';
    };
    const formatAstragal = (item: any): string => {
      const payload =
        (item as any)?.windowGeneratorPayload ||
        (item as any)?.extras?.windowGeneratorPayload ||
        null;
      const pres = payload?.presentation || {};
      const rawOn =
        (item as any)?.astragalOn ??
        (item as any)?.extras?.astragalOn ??
        (item as any)?.astragalBars ??
        (item as any)?.extras?.astragalBars ??
        pres?.aBarOn ??
        pres?.astragalBars ??
        pres?.astragal_bars;
      const on = boolFromUnknown(rawOn);
      if (on !== true) return 'None';

      const cols =
        numFromUnknown((item as any)?.astragalCols) ??
        numFromUnknown((item as any)?.extras?.astragalCols) ??
        numFromUnknown((item as any)?.astragal_cols) ??
        numFromUnknown((item as any)?.extras?.astragal_cols) ??
        numFromUnknown(pres?.aBarCols);
      const rows =
        numFromUnknown((item as any)?.astragalRows) ??
        numFromUnknown((item as any)?.extras?.astragalRows) ??
        numFromUnknown((item as any)?.astragal_rows) ??
        numFromUnknown((item as any)?.extras?.astragal_rows) ??
        numFromUnknown(pres?.aBarRows);
      const widthMm =
        numFromUnknown((item as any)?.astragalWidthMm) ??
        numFromUnknown((item as any)?.extras?.astragalWidthMm) ??
        numFromUnknown((item as any)?.astragal_width_mm) ??
        numFromUnknown((item as any)?.extras?.astragal_width_mm) ??
        numFromUnknown(pres?.aBarWidth);
      const offX =
        numFromUnknown((item as any)?.astragalOffXPct) ??
        numFromUnknown((item as any)?.extras?.astragalOffXPct) ??
        numFromUnknown((item as any)?.astragal_offset_x_pct) ??
        numFromUnknown((item as any)?.extras?.astragal_offset_x_pct) ??
        numFromUnknown(pres?.aBarOffX);
      const offY =
        numFromUnknown((item as any)?.astragalOffYPct) ??
        numFromUnknown((item as any)?.extras?.astragalOffYPct) ??
        numFromUnknown((item as any)?.astragal_offset_y_pct) ??
        numFromUnknown((item as any)?.extras?.astragal_offset_y_pct) ??
        numFromUnknown(pres?.aBarOffY);

      const grid =
        cols != null && rows != null && cols >= 0 && rows >= 0
          ? `${Math.round(cols)}x${Math.round(rows)}`
          : '';
      const width = widthMm != null && widthMm > 0 ? `${Math.round(widthMm)}mm` : '';
      const offset =
        (offX != null && offX !== 0) || (offY != null && offY !== 0)
          ? `offset ${Math.round(offX || 0)},${Math.round(offY || 0)}`
          : '';
      const text = [grid, width, offset].filter(Boolean).join(' ');
      return text || 'Enabled';
    };
    const specRow = (label: string, value: unknown): string => (!String(value ?? '').trim() ? '' : `
      <tr>
        <td class="supplier-pdf-spec-label">${escapeHtml(label)}:</td>
        <td class="supplier-pdf-spec-value${String(label).toUpperCase() === 'REFERENCE' ? ' supplier-pdf-spec-value-reference' : ''}">${escapeHtml(value)}</td>
      </tr>
    `);
    const itemSections = itemsForPdf.map((item: any, index: number) => {
      const extras = (item as any)?.extras || {};
      const bayImage = pick(item, 'imageBaySchematic');
      const bayTangent = pick(item, 'bayTangentWidthMm');
      const bayProjectionDepth = pick(item, 'bayProjectionDepthMm');
      const bayFacetWidths = pick(item, 'bayFacetWidthsMm');
      const bayJointAngles = pick(item, 'bayJointAnglesDeg');
      const isBay = !!(bayImage || bayTangent || bayProjectionDepth || bayFacetWidths || bayJointAngles);
      const width = (item as any)?.width || extras?.width;
      const height = (item as any)?.height || extras?.height;
      const size = isBay
        ? (height ? `H${mm(height)}` : '')
        : [width ? `W${mm(width)}` : '', height ? `H${mm(height)}` : ''].filter(Boolean).join(' x ');
      const summaryValue = summaryValueFor(item);
      const externalColour = summaryValue('frame_color_external');
      const internalColour = summaryValue('frame_color_internal');
      const colourRows = (externalColour || internalColour)
        ? [specRow('EXTERNAL COLOUR', externalColour), specRow('INTERNAL COLOUR', internalColour)]
        : [specRow('COLOUR', summaryValue('frame_color_combined') || pick(item, 'frameColor') || pick(item, 'windowColour'))];
      const leadingSpecRows = [
        specRow('REFERENCE', pick(item, 'reference') || (item as any)?.glassReference || `Window-${index + 1}`),
        specRow('TYPE', summaryValue('window_type_name') || titleCase(pick(item, 'windowType') || (item as any)?.windowType || 'Casement')),
        specRow('PROFILE', summaryValue('variant') || pick(item, 'profileType')),
        specRow('GLASS', summaryValue('glass_type') || (item as any)?.glassType || extras?.glassType || (item as any)?.thickness || ''),
        specRow('GLASS SPECIFICATION', summaryValue('glass_specification')),
        specRow('BEAD TYPE', summaryValue('beading_type') || pick(item, 'beadingType')),
        specRow('MATERIAL', summaryValue('material_name') || pick(item, 'windowMaterial') || pick(item, 'material')),
        specRow('ASTRAGAL', formatAstragal(item)),
        specRow('ASTRAGAL BAR', pick(item, 'astragalBarSide') || 'None (default)'),
        specRow('GEORGIAN', summaryValue('georgian_bars')),
        specRow('QUANTITY', qtyFor(item)),
      ];
      const finishingSpecRows = [
        ...colourRows,
        specRow('HANDLE', summaryValue('handle_style') || pick(item, 'handleStyle')),
        specRow('HANDLE COLOUR', summaryValue('handle_color') || pick(item, 'handleColor') || pick(item, 'handleColour')),
        specRow('HINGE', summaryValue('hinge_placement')),
        specRow('HINGE TYPE', summaryValue('window_hinge_type')),
        specRow('OPEN DIRECTION', summaryValue('open_direction')),
        specRow('MASTER LEAF', summaryValue('master_leaf')),
        specRow('PANELS', summaryValue('panel_count')),
        specRow('STACK', summaryValue('stack_direction')),
        specRow('TRAFFIC DOOR', summaryValue('traffic_door')),
        specRow('SLIDING', summaryValue('sliding_panels')),
        specRow('THRESHOLD?', summaryValue('threshold')),
        specRow('TRICKLE VENTS', summaryValue('trickle_vents')),
        specRow('CILL', summaryValue('cill_option')),
        (((item as any)?.notes || extras?.notes) ? specRow('NOTES', (item as any)?.notes || extras?.notes || '') : ''),
      ];
      const baySizeRows = isBay
        ? [
            specRow('SIZE', size),
            bayTangent ? specRow('TANGENT WIDTH', `${bayTangent}mm`) : '',
            bayProjectionDepth ? specRow('PROJECTION DEPTH', `${bayProjectionDepth}mm`) : '',
            bayJointAngles ? specRow('JOINT ANGLES (DEG)', bayJointAngles) : '',
            bayFacetWidths ? specRow('FACET WIDTHS (MM)', bayFacetWidths) : '',
          ]
        : [specRow('SIZE', size)];
      const specRows = [
        ...leadingSpecRows,
        ...baySizeRows,
        specRow('EXTENDERS (MM)', summaryValue('extenders_mm')),
        ...finishingSpecRows,
      ].filter(Boolean).join('');
      const outsideDesignSrc =
        pick(item, 'imageDesignOutside') ||
        pick(item, 'imagePresentationOutside') ||
        (item as any)?.imagePath ||
        extras?.imageUrl;
      // Bay schematic + spec cannot share a page-break-inside:avoid section; spec is a sibling page.
      const hasBaySchematic = !!(isBay && bayImage);
      const specBlock = `
          <div class="supplier-pdf-spec-wrap">
            <table class="supplier-pdf-spec-table" role="presentation">
              <tbody>${specRows}</tbody>
            </table>
          </div>
      `;
      return `
        <section class="supplier-pdf-page${index > 0 ? ' page-break-before' : ''}${hasBaySchematic ? ' supplier-pdf-images-page' : ''}">
          <div class="supplier-pdf-top-design">
            <div class="supplier-pdf-top-frame">
              ${outsideDesignSrc ? `<img src="${escapeHtml(pdfImageSrc(outsideDesignSrc))}" alt="Design Outside" />` : ''}
            </div>
            <div class="supplier-pdf-image-label">Design · Outside</div>
          </div>
          ${hasBaySchematic ? `
            <div class="supplier-pdf-bay-plan">
              <div class="supplier-pdf-bay-frame">
                <img src="${escapeHtml(pdfImageSrc(bayImage))}" alt="Bay schematic" />
              </div>
              <div class="supplier-pdf-image-label">Bay Schematic</div>
            </div>
          ` : ''}
          ${hasBaySchematic ? '' : specBlock}
        </section>
        ${hasBaySchematic ? `
        <section class="supplier-pdf-page supplier-pdf-spec-page page-break-before">
          ${specBlock}
        </section>
        ` : ''}
      `;
    }).join('');
    const appendixSections = itemsForPdf.map((item: any, index: number) => {
          const extras = (item as any)?.extras || {};
          const ref = pick(item, 'reference') || (item as any)?.glassReference || `Window-${index + 1}`;
          const presExtSrc =
            pick(item, 'imagePresentationOutside') ||
            pick(item, 'imageDesignOutside') ||
            (item as any)?.imagePath ||
            extras?.imageUrl;
          const presIntSrc =
            pick(item, 'imagePresentationInside') ||
            pick(item, 'imageDesignInside') ||
            (item as any)?.imagePathInternal ||
            extras?.imageUrlInternal;
          return `
            <section class="supplier-pdf-page supplier-pdf-appendix-page page-break-before">
              <div class="supplier-pdf-appendix-title">Presentation Appendix</div>
              <div class="supplier-pdf-appendix-ref">
                <span class="supplier-pdf-appendix-ref-label">Reference:</span>
                <span class="supplier-pdf-appendix-ref-value">${escapeHtml(ref)}</span>
              </div>
              <div class="supplier-pdf-appendix-grid">
                <div class="supplier-pdf-appendix-cell">
                  <div class="supplier-pdf-appendix-frame">
                    ${presExtSrc ? `<img src="${escapeHtml(pdfImageSrc(presExtSrc))}" alt="Presentation Outside" />` : ''}
                  </div>
                  <div class="supplier-pdf-image-label">Presentation · Outside</div>
                </div>
                <div class="supplier-pdf-appendix-cell">
                  <div class="supplier-pdf-appendix-frame">
                    ${presIntSrc ? `<img src="${escapeHtml(pdfImageSrc(presIntSrc))}" alt="Presentation Inside" />` : ''}
                  </div>
                  <div class="supplier-pdf-image-label">Presentation · Inside</div>
                </div>
              </div>
            </section>
          `;
        }).join('');
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${escapeHtml(subject)}</title>
  <style>
    html, body { margin:0; padding:0; font-family: Arial, Helvetica, sans-serif; color:#0f172a; }
    .supplier-pdf-page {
      page-break-inside: avoid;
      break-inside: avoid;
      display:flex;
      flex-direction:column;
      min-height:920px;
      height:920px;
      box-sizing:border-box;
    }
    .page-break-before { page-break-before: always; break-before: page; }
    .supplier-pdf-top-design { margin:0 0 12px 0; flex:0 0 auto; }
    .supplier-pdf-top-frame,
    .supplier-pdf-inside-frame,
    .supplier-pdf-bay-frame {
      background:#d9dee7;
      border:1px solid #cbd3df;
      overflow:hidden;
      margin:0 auto;
      display:flex;
      align-items:center;
      justify-content:center;
    }
    .supplier-pdf-top-frame { width:100%; height:520px; }
    .supplier-pdf-images-page .supplier-pdf-top-frame { height:480px; }
    .supplier-pdf-top-frame img,
    .supplier-pdf-bay-frame img { max-width:100%; max-height:100%; display:block; }
    .supplier-pdf-spec-wrap { margin-top:10px; padding-top:8px; }
    .supplier-pdf-image-label { font-size:8px; color:#334155; text-align:center; margin-top:3px; }
    .supplier-pdf-bay-plan { margin:12px auto 0 auto; text-align:center; page-break-inside:avoid; break-inside:avoid; }
    .supplier-pdf-bay-frame { width:100%; height:280px; max-height:280px; }
    .supplier-pdf-spec-page { height:auto; min-height:920px; }
    .supplier-pdf-spec-table { width:100%; border-collapse:collapse; table-layout:fixed; font-size:9.6px; line-height:1.1; }
    .supplier-pdf-spec-label, .supplier-pdf-spec-value { padding:4px 6px; border-bottom:1px solid #d8dee8; vertical-align:top; }
    .supplier-pdf-spec-label { width:34%; font-weight:700; color:#111827; white-space:nowrap; }
    .supplier-pdf-spec-value { width:66%; color:#111827; }
    .supplier-pdf-spec-value-reference { font-weight:700; text-transform:uppercase; letter-spacing:0.02em; }
    .supplier-pdf-appendix-page { justify-content:flex-start; }
    .supplier-pdf-appendix-title { font-size:15px; font-weight:700; color:#0f172a; margin:2px 0 8px 0; }
    .supplier-pdf-appendix-ref { font-size:11px; color:#334155; margin:0 0 10px 0; }
    .supplier-pdf-appendix-ref-label,
    .supplier-pdf-appendix-ref-value {
      font-weight:700;
      text-transform:uppercase;
      letter-spacing:0.02em;
    }
    .supplier-pdf-appendix-grid { display:grid; grid-template-columns:1fr 1fr; gap:16px; align-items:start; }
    .supplier-pdf-appendix-cell { min-width:0; }
    .supplier-pdf-appendix-frame {
      width:100%;
      height:360px;
      background:#d9dee7;
      border:1px solid #cbd3df;
      overflow:hidden;
      display:flex;
      align-items:center;
      justify-content:center;
    }
    .supplier-pdf-appendix-frame img { max-width:100%; max-height:100%; display:block; }
    @media print {
      .page-break-before { page-break-before: always !important; break-before: page !important; }
      .supplier-pdf-page { page-break-inside: avoid; break-inside: avoid; }
      .supplier-pdf-spec-page { page-break-inside: auto; break-inside: auto; }
    }
  </style>
</head>
<body>${itemSections}${appendixSections}</body>
</html>`;
  };

  const buildPdfPayload = async (orderForEmail: any) => {
    const subject = await getWindowOrderEmailSubject(orderForEmail as any);
    const buildPdfHeaderFooterOptions = (
      opts: { headerImagePath?: string | null; footerImagePath?: string | null; footerHeightPx?: number }
    ) => {
      const headerSrc = toAbsoluteAssetUrl(opts.headerImagePath || '');
      const footerSrc = toAbsoluteAssetUrl(opts.footerImagePath || '');
      const sideMarginPx = 36; // ~Word "Narrow" (0.5in) left/right
      const headerHeightPx = 92;
      const footerHeightPx = opts.footerHeightPx ?? 66;
      const headerGapPx = 16;
      const footerGapPx = 14;
      const topMarginBorrowPx = 10;
      const topMarginPx = headerSrc
        ? Math.max(sideMarginPx, (headerHeightPx + headerGapPx - topMarginBorrowPx))
        : sideMarginPx;
      const bottomMarginPx = footerSrc ? (footerHeightPx + footerGapPx) : sideMarginPx;
      const headerTemplate = headerSrc
        ? `<div style="width:100%;height:${headerHeightPx}px;padding:0 ${sideMarginPx}px;box-sizing:border-box;">
<img src="${headerSrc}" alt="PDF Header" style="display:block;width:100%;height:100%;object-fit:contain;" />
</div>`
        : '';
      const footerTemplate = footerSrc
        ? `<div style="width:100%;height:${footerHeightPx}px;padding:0 ${sideMarginPx}px;box-sizing:border-box;">
<img src="${footerSrc}" alt="PDF Footer" style="display:block;width:100%;height:100%;object-fit:contain;" />
</div>`
        : '';
      return {
        displayHeaderFooter: !!(headerTemplate || footerTemplate),
        headerTemplate,
        footerTemplate,
        margin: {
          top: `${topMarginPx}px`,
          right: `${sideMarginPx}px`,
          bottom: `${bottomMarginPx}px`,
          left: `${sideMarginPx}px`,
        },
      };
    };
    const perItemPagedHtml = buildDedicatedSupplierPdfHtml(orderForEmail, subject);
    const pdfConfig =
      organisationId && documentKind
        ? await pdfExportConfigService.getConfigByOrganisation(organisationId, documentKind).catch((err) => {
            console.warn('Could not load PDF export config for branded images:', err);
            return null;
          })
        : null;
    const pdfOptions = buildPdfHeaderFooterOptions({
      headerImagePath: pdfConfig?.headerImagePath,
      footerImagePath: pdfConfig?.footerImagePath,
      footerHeightPx: await pdfFooterHeightPx(pdfConfig?.footerImagePath),
    });
    return { html: perItemPagedHtml, pdfOptions };
  };

  const generatePdfBlob = async (orderForEmail: any): Promise<Blob> => {
    const payload = await buildPdfPayload(orderForEmail);
    const resp = await fetch('/api/export-pdf', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!resp.ok) {
      const msg = await resp.text().catch(() => '');
      throw new Error(msg || `PDF export failed (${resp.status})`);
    }
    return await resp.blob();
  };

  const testPdfExport = async () => {
    try {
      setStatus({ kind: 'idle' });
      setIsExportingPdf(true);
      const supplier = supplierId ? await fetchSupplier() : null;
      const hydratedItems = await ensureSupplierPdfImages(items);
      const itemsForEmail = convertWindowItemsForOrder(hydratedItems);
      const orderForEmail = buildTransientOrder(
        { name: customerName || customerReference, email: '', phone: '', customerReference },
        itemsForEmail as any,
        0,
        0,
        0,
        (supplier || undefined) as any,
        supplier ? ([supplier] as any) : undefined
      );
      const blob = await generatePdfBlob(orderForEmail);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = buildSupplierPdfFileName({ customerReference, customerName });
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setStatus({ kind: 'success', message: 'PDF downloaded successfully.' });
    } catch (e) {
      console.error('Failed to export PDF:', e);
      setStatus({ kind: 'error', message: e instanceof Error ? e.message : 'Failed to export PDF' });
    } finally {
      setIsExportingPdf(false);
    }
  };

  const fetchSupplier = async (): Promise<SupplierRow> => {
    const { data, error } = await directSupabase
      .from('suppliers')
      .select('*')
      .eq('id', supplierId)
      .single();

    if (error) throw new Error(error.message);
    if (!data) throw new Error('Supplier not found');
    return data as SupplierRow;
  };

  const trackingMetadata = (channel: 'email_format' | 'email_attachment', supplier: SupplierRow | null) => {
    if (!projectId) return null;
    return buildSupplierTransmissionMetadata({
      projectId,
      surveyId,
      surveyLabel,
      documentId,
      documentVersionId: documentVersionId || activeVersionId,
      documentVersionNumber,
      supplierId: supplier?.id || supplierId || null,
      supplierName: supplier?.supplier_name || null,
      channel,
      itemCount: items.length,
    });
  };

  const logAttachmentTransmission = async (args: {
    supplier: SupplierRow | null;
    status: 'draft' | 'failed';
    subject: string;
    html: string;
    errorMessage?: string | null;
    pdfFileName?: string;
  }) => {
    const metadata = trackingMetadata('email_attachment', args.supplier);
    if (!metadata) return;
    await emailActivity.log({
      template_type: 'windowOrder',
      subject: args.subject,
      recipient: args.supplier?.email || '',
      status: args.status,
      error_message: args.errorMessage || null,
      content_html: args.html,
      content_text: args.html.replace(/<[^>]*>/g, ''),
      metadata,
    });
    if (args.status !== 'failed') onTransmitted?.();
  };

  const send = async (mode: 'draft' | 'send') => {
    try {
      setStatus({ kind: 'idle' });
      const supplier = supplierId ? await fetchSupplier() : null;
      if (mode === 'send') {
        if (!supplierId) throw new Error('Please select a supplier');
        if (!supplier?.email) throw new Error('Selected supplier does not have an email address configured');
      }

      if (mode === 'draft') setIsCreatingDraft(true);
      else setIsSending(true);

      const signedIn = await isSignedIn();
      if (!signedIn) {
        const ok = await signIn();
        if (!ok) throw new Error('Please sign in to your Microsoft account to continue');
      }

      // Keep draft body visuals aligned with latest saved modal edits as well.
      // If live regeneration fails, fall back to stored URLs so draft still opens.
      const sourceItemsForEmail = await ensureSupplierPdfImages(items).catch(() => items);
      const itemsForEmail = convertWindowItemsForOrder(sourceItemsForEmail);

      // Avoid persisting VD export payloads (often large data-URL images) into localStorage,
      // which can exceed browser quota and break supplier send flows.
      const orderForEmail = buildTransientOrder(
        { name: customerName || customerReference, email: '', phone: '', customerReference },
        itemsForEmail as any,
        0,
        0,
        0,
        (supplier || undefined) as any,
        supplier ? ([supplier] as any) : undefined
      );
      const extraMetadata = trackingMetadata(mode === 'draft' ? 'email_format' : 'email_attachment', supplier);
      if (mode === 'draft') {
        await sendOrderEmail(orderForEmail as any, supplier?.email || '', true, true, extraMetadata || undefined);
        if (extraMetadata) onTransmitted?.();
        if (projectId) {
          emitActivityEventBackground({
            action: 'supplier.order_sent',
            entityType: 'email',
            entityId: documentId || null,
            projectId,
            label: supplier?.supplier_name
              ? `Supplier order draft created (${supplier.supplier_name})`
              : 'Supplier order draft created',
            metadata: {
              mode: 'draft',
              supplierId: supplier?.id || supplierId || null,
              surveyId: surveyId || null,
            },
          });
        }
      } else {
        if (!supplier?.email) throw new Error('Selected supplier does not have an email address configured');
        const subject = await getWindowOrderEmailSubject(orderForEmail as any);
        const draftContent = buildSupplierPdfDraftContent({
          baseSubject: subject,
          organizationName: organisationName || undefined,
          customerName,
          customerReference,
        });
        const pdfFileName = buildSupplierPdfFileName({ customerReference, customerName });
        try {
          const pdfBlob = await generatePdfBlob(orderForEmail);
          const pdfBase64 = arrayBufferToBase64(await pdfBlob.arrayBuffer());
          const client = await getGraphClient();
          await client.api('/me/messages').post({
            subject: draftContent.subject,
            body: {
              contentType: 'HTML',
              content: draftContent.htmlBody,
            },
            sender: {
              emailAddress: {
                address: 'info@keystoneglazing.co.uk',
                name: 'Keystone Glazing',
              },
            },
            toRecipients: [
              {
                emailAddress: {
                  address: supplier.email,
                },
              },
            ],
            attachments: [
              {
                '@odata.type': '#microsoft.graph.fileAttachment',
                name: pdfFileName,
                contentType: 'application/pdf',
                contentBytes: pdfBase64,
              },
            ],
          });
          await logAttachmentTransmission({
            supplier,
            status: 'draft',
            subject: draftContent.subject,
            html: draftContent.htmlBody,
            pdfFileName,
          });
          if (projectId) {
            emitActivityEventBackground({
              action: 'supplier.order_sent',
              entityType: 'email',
              entityId: documentId || null,
              projectId,
              label: supplier?.supplier_name
                ? `Supplier order sent (${supplier.supplier_name})`
                : 'Supplier order sent',
              metadata: {
                mode: 'send',
                supplierId: supplier?.id || supplierId || null,
                surveyId: surveyId || null,
                pdfFileName,
              },
            });
          }
        } catch (sendErr) {
          await logAttachmentTransmission({
            supplier,
            status: 'failed',
            subject: draftContent.subject,
            html: draftContent.htmlBody,
            errorMessage: sendErr instanceof Error ? sendErr.message : 'Failed to send',
            pdfFileName,
          });
          throw sendErr;
        }
      }

      setStatus({
        kind: 'success',
        message: mode === 'draft'
          ? 'Draft email created! You can find it in your Outlook Drafts folder.'
          : 'Draft email with PDF attachment created! You can find it in your Outlook Drafts folder.',
      });
    } catch (e) {
      console.error('Failed to send to supplier:', e);
      setStatus({ kind: 'error', message: e instanceof Error ? e.message : 'Failed to send' });
    } finally {
      setIsCreatingDraft(false);
      setIsSending(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        sx={{
          maxWidth: 720,
          width: { xs: '100vw', sm: '95vw' },
          maxHeight: { xs: '100vh', sm: '90vh' },
          m: { xs: 0, sm: 'auto' },
          borderRadius: { xs: 0, sm: 'md' },
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <DialogTitle>Send to supplier</DialogTitle>
        <DialogContent
          sx={{
            flex: 1,
            overflowY: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          <Stack spacing={2} sx={{ p: { xs: 1, sm: 2 } }}>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              This sends {surveyLabel ? `${surveyLabel} ` : 'your Visual Designer selections '}
              to a window supplier using your saved window-order email template.
            </Typography>

            {isLoadingLines && (
              <Alert color="neutral" startDecorator={<CircularProgress size="sm" />}>
                Loading latest saved Visual Designer items from this document...
              </Alert>
            )}

            {linesLoadError && (
              <Alert color="danger">{linesLoadError}</Alert>
            )}

            <SheetSummary
              customerReference={customerReference}
              totalQty={summary.totalQty}
              windowQty={summary.windowQty}
              doorQty={summary.doorQty}
            />

            {failedImages.length > 0 && (
              <Alert
                color="warning"
                endDecorator={
                  <Checkbox
                    checked={ignoreMissingImages}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIgnoreMissingImages(e.target.checked)}
                    label="Continue anyway"
                  />
                }
              >
                <strong>Warning:</strong> One or more windows are missing a preview image. This may result in placeholder images in the email.
              </Alert>
            )}

            <Box>
              <Typography level="title-sm" mb={1}>Select Window Supplier</Typography>
              <SupplierSelector
                value={supplierId}
                onChange={setSupplierId}
                supplierType="Windows"
              />
            </Box>

            {status.kind === 'success' && (
              <Alert color="success">{status.message}</Alert>
            )}
            {status.kind === 'error' && (
              <Alert color="danger">{status.message}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Button variant="outlined" color="neutral" onClick={onClose}>
            Close
          </Button>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            <Button
              variant="soft"
              color="neutral"
              disabled={shouldDisablePdfTest()}
              startDecorator={isExportingPdf ? <CircularProgress size="sm" /> : null}
              onClick={testPdfExport}
            >
              Download PDF
            </Button>
            <Button
              variant="outlined"
              color="primary"
              disabled={shouldDisableDraft()}
              startDecorator={isCreatingDraft ? <CircularProgress size="sm" /> : null}
              onClick={() => send('draft')}
            >
              E-mail Format
            </Button>
            <Button
              color="primary"
              disabled={shouldDisableSend()}
              startDecorator={isSending ? <CircularProgress size="sm" /> : null}
              onClick={() => send('send')}
            >
              E-mail Attachment
            </Button>
          </Stack>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
};

const SheetSummary: React.FC<{
  customerReference: string;
  totalQty: number;
  windowQty: number;
  doorQty: number;
}> = ({ customerReference, totalQty, windowQty, doorQty }) => {
  return (
    <Box sx={{ p: 1.25, borderRadius: 'sm', bgcolor: 'background.level1', border: '1px solid', borderColor: 'divider' }}>
      <Stack spacing={0.5}>
        <Typography level="body-sm"><strong>Reference:</strong> {customerReference}</Typography>
        <Typography level="body-sm"><strong>Total quantity:</strong> {totalQty}</Typography>
        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
          {windowQty} window{windowQty === 1 ? '' : 's'} • {doorQty} door{doorQty === 1 ? '' : 's'}
        </Typography>
      </Stack>
    </Box>
  );
};

export default SendVisualDesignerToSupplierModal;
